import React, { useState, useRef, useEffect } from "react";
import SideBar from "../../Components/Sidebar";
import Pageheader from "../Container/PageHeader/Pageheader";
import ChatModal from "../ChatModal";
import Searchbar from "../../Components/Searchbar";
import { useSelector } from "react-redux";
import { getFraudReviewPayments } from "../../Services/PaymentsApi";
import "./AdminPaymentsReview.css";
import Loader from "../../Components/Loader";

const AdminPaymentsReview = () => {
  const value = useSelector((state) => state?.value);
  const [payments, setPayments] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentCounts, setPaymentCounts] = useState({
    all: 0,
    allowed: 0,
    refunded: 0,
    underreview: 0,
    blocked: 0
  });

  const getStatusBadgeClass = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "approved":
      case "allowed": 
        return "status-badge-allowed";
      case "refunded": 
        return "status-badge-refunded";
      case "underreview":
      case "under_review": 
        return "status-badge-review";
      case "blocked": 
        return "status-badge-blocked";     
      default: 
        return "";
    }
  };

  const getStatusText = (status) => {
    const statusLower = status?.toLowerCase();
    switch (statusLower) {
      case "approved":
      case "allowed": 
        return "Allowed";
      case "refunded": 
        return "Refunded";
      case "underreview":
      case "under_review": 
        return "Under Review";
      case "blocked": 
        return "Blocked";     
      default: 
        return status;
    }
  };

  const normalizeStatus = (status) => {
    const statusLower = status?.toLowerCase();
    if (statusLower === "approved") return "allowed";
    if (statusLower === "underreview") return "under_review";
    return statusLower;
  };

  const fetchPayments = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await getFraudReviewPayments(filterStatus, searchTerm);
      
      const normalizedPayments = (response.data || []).map(payment => ({
        ...payment,
        status: normalizeStatus(payment.status),
        date: payment.paymentDate ? new Date(payment.paymentDate).toISOString().split('T')[0] : 'N/A'
      }));
      
      setPayments(normalizedPayments);
      
      if (filterStatus === 'all' && !searchTerm) {
        setAllPayments(normalizedPayments);
        
        const counts = {
          all: normalizedPayments.length,
          allowed: normalizedPayments.filter(p => normalizeStatus(p.status) === 'allowed').length,
          refunded: normalizedPayments.filter(p => normalizeStatus(p.status) === 'refunded').length,
          underreview: normalizedPayments.filter(p => normalizeStatus(p.status) === 'under_review').length,
          blocked: normalizedPayments.filter(p => normalizeStatus(p.status) === 'blocked').length
        };
        setPaymentCounts(counts);
      }
    } catch (err) {
      console.error("Error fetching payments:", err);
      setError("Failed to load payments. Please try again.");
      setPayments([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [filterStatus, searchTerm]);

  const handleFilterChange = (newFilter) => {
    setFilterStatus(newFilter);
    setSearchTerm("");
    setExpandedRow(null);
  };

  const getRiskLevelClass = (level) => {
    switch (level) {
      case "normal": return "risk-level-normal";
      case "elevated": return "risk-level-elevated";
      case "highest": return "risk-level-highest";
      default: return "";
    }
  };

  const getRiskLevelText = (level) => {
    switch (level) {
      case "normal": return "Normal";
      case "elevated": return "Elevated";
      case "highest": return "Highest";
      default: return level;
    }
  };

  const handleViewInStripe = (paymentIntentId, status) => {
    const url = normalizeStatus(status) === 'blocked' 
      ? `https://dashboard.stripe.com/payments/${paymentIntentId}`
      : `https://dashboard.stripe.com/radar/reviews?payment_intent=${paymentIntentId}`;
    window.open(url, '_blank');
  };

  const toggleRowExpansion = (id, status) => {
    setExpandedRow(expandedRow === id ? null : id);
  };

  const filteredPayments = payments;

  return (
    <>
      <div className='theme-bg clr-white height-100' style={{ display: "flex", overflow: 'hidden' }}>
        <SideBar />
        <div className="Page-main-container">
          <Pageheader pageTitle="Admin - Payment Review" />
          <div className="admin-payment-container">
            
            <div className="admin-filter-section">
              <div className="admin-filter-buttons">
                <button 
                  className={`admin-filter-btn ${filterStatus === "all" ? "active" : ""}`}
                  onClick={() => handleFilterChange("all")}
                >
                  All Payments ({paymentCounts.all})
                </button>
                <button 
                  className={`admin-filter-btn ${filterStatus === "allowed" ? "active" : ""}`}
                  onClick={() => handleFilterChange("allowed")}
                >
                  🟢 Allowed ({paymentCounts.allowed})
                </button>
                <button 
                  className={`admin-filter-btn ${filterStatus === "refunded" ? "active" : ""}`}
                  onClick={() => handleFilterChange("refunded")}
                >
                  🔵 Refunded ({paymentCounts.refunded})
                </button>
                <button 
                  className={`admin-filter-btn ${filterStatus === "underreview" ? "active" : ""}`}
                  onClick={() => handleFilterChange("underreview")}
                >
                  🟡 Under Review ({paymentCounts.underreview})
                </button>
                <button 
                  className={`admin-filter-btn ${filterStatus === "blocked" ? "active" : ""}`}
                  onClick={() => handleFilterChange("blocked")}
                >
                  🔴 Blocked ({paymentCounts.blocked})
                </button>               
              </div>
              
              <div className="admin-search-container">
                <Searchbar
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  placeholder="Search by name, amount..."
                  alwaysShowIcon={true}
                  customStyles={{
                    width: '100%',
                    maxWidth: '37.04vh'
                  }}
                />
              </div>
            </div>

            <div className="admin-table-container">
              {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                  <Loader message="Loading payments..." />
                </div>
              ) : error ? (
                <div className="admin-error">
                  <div className="admin-error-icon">⚠️</div>
                  <div className="admin-error-text">{error}</div>
                  <button 
                    className="admin-retry-btn"
                    onClick={fetchPayments}
                  >
                    Retry
                  </button>
                </div>
              ) : filteredPayments.length === 0 ? (
                <div className="admin-no-results">
                  <div className="admin-no-results-icon">🔍</div>
                  <div className="admin-no-results-text">
                    {searchTerm ? `No payments found matching "${searchTerm}"` : "No payments found"}
                  </div>
                  {searchTerm && (
                    <button 
                      className="admin-clear-search-btn"
                      onClick={() => setSearchTerm("")}
                    >
                      Clear Search
                    </button>
                  )}
                </div>
              ) : (
                <>
                  {searchTerm && (
                    <div className="admin-results-count">
                      Showing {filteredPayments.length} result{filteredPayments.length !== 1 ? 's' : ''}
                    </div>
                  )}
                  <div className="admin-table-wrapper">
                    <table className="admin-payment-table">
                  <thead>
                    <tr>
                      <th>Sender Name</th>
                      <th>Receiver Name</th>
                      <th>Amount</th>
                      <th>Payment Intent ID</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map((payment) => (
                      <React.Fragment key={payment.id}>
                        <tr 
                          className={`admin-table-row ${expandedRow === payment.id ? "expanded" : ""}`}
                          onClick={() => toggleRowExpansion(payment.id, payment.status)}
                          style={{ cursor: "pointer" }}
                        >
                          <td className="admin-td-name">{payment.senderName}</td>
                          <td className="admin-td-name">{payment.receiverName}</td>
                          <td className="admin-td-amount">${payment.amount}</td>
                          <td className="admin-td-payment-id">{payment.paymentIntentId}</td>
                          <td className="admin-td-date">{payment.date}</td>
                          <td>
                            <span className={`status-badge ${getStatusBadgeClass(payment.status)}`}>
                              {getStatusText(payment.status)}
                            </span>
                          </td>
                          <td className="admin-td-actions">
                            <button 
                              className="admin-action-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleViewInStripe(payment.paymentIntentId, payment.status);
                              }}
                            >
                              View in Stripe
                            </button>
                          </td>
                        </tr>
                        
                        {expandedRow === payment.id && (normalizeStatus(payment.status) === "allowed" || normalizeStatus(payment.status) === "refunded" || normalizeStatus(payment.status) === "under_review" || normalizeStatus(payment.status) === "blocked") && (
                          <tr className="admin-expanded-row">
                            <td colSpan="7">
                              <div className="admin-expanded-content">
                                {normalizeStatus(payment.status) === "allowed" && (
                                  <div className="admin-status-details allowed-details">
                                    <div className="admin-status-icon">🟢</div>
                                    <div className="admin-status-info">
                                      <h4>Payment Allowed</h4>
                                      <div className="admin-detail-row">
                                        <span className="admin-detail-label">Approved Date:</span>
                                        <span className="admin-detail-value">
                                          {payment.approvedDate ? new Date(payment.approvedDate).toISOString().split('T')[0] : 'N/A'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {normalizeStatus(payment.status) === "refunded" && (
                                  <div className="admin-status-details refunded-details">
                                    <div className="admin-status-icon">🔵</div>
                                    <div className="admin-status-info">
                                      <h4>Payment Refunded</h4>
                                      <div className="admin-detail-row">
                                        <span className="admin-detail-label">Refund Date:</span>
                                        <span className="admin-detail-value">
                                          {payment.refundedDate ? new Date(payment.refundedDate).toISOString().split('T')[0] : 'N/A'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {(normalizeStatus(payment.status) === "under_review" || normalizeStatus(payment.status) === "blocked") && (
                                  <div className={`admin-status-details ${normalizeStatus(payment.status) === "under_review" ? "review-details" : "blocked-details"}`}>
                                    <div className="admin-status-icon">{normalizeStatus(payment.status) === "under_review" ? "🟡" : "🔴"}</div>
                                    <div className="admin-status-info">
                                      <h4>{normalizeStatus(payment.status) === "under_review" ? "Under Review" : "Payment Blocked"}</h4>
                                      <div className="admin-detail-row">
                                        <span className="admin-detail-label">Risk Score:</span>
                                        <span className="admin-detail-value">
                                          {payment.riskScore !== undefined ? payment.riskScore : 'N/A'}
                                        </span>
                                      </div>
                                      <div className="admin-detail-row">
                                        <span className="admin-detail-label">Risk Level:</span>
                                        <span className={`admin-detail-value ${getRiskLevelClass(payment.riskLevel)}`}>
                                          {getRiskLevelText(payment.riskLevel)}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
              </>
              )}
            </div>
          </div>
        </div>
      </div>
      <ChatModal open={value?.isChatOpen} />
    </>
  );
};

export default AdminPaymentsReview;
